import { Link, Navigate, useParams } from 'react-router-dom';
import {
  NEIGHBOURHOODS,
  type NeighbourhoodSlug,
} from '../lib/neighbourhoods.js';
import { CITIES } from '../lib/cities.js';
import { usePageMeta } from '../lib/usePageMeta.js';
import { LiveListingsStrip } from '../components/LiveListingsStrip.js';

const SITE_ORIGIN = 'https://towshare.ca';

export function NeighbourhoodPage() {
  const { city, neighbourhood } = useParams<{ city: string; neighbourhood: string }>();
  const slug = (neighbourhood ?? '').toLowerCase() as NeighbourhoodSlug;
  const data = NEIGHBOURHOODS[slug];

  if (!data || city !== data.parentCitySlug) {
    return <Navigate to="/" replace />;
  }

  const parentCity = CITIES[data.parentCitySlug];
  const canonicalPath = `/trailer-rental/${data.parentCitySlug}/${data.slug}`;
  const title = `Trailer rental ${data.name} — utility, dump & enclosed`;
  const description = `Rent utility, dump, enclosed, and car-hauler trailers from neighbours in ${data.fullName}. Same-day pickup, $1,500 damage guarantee, Stripe-secured. No yard drive, no lineup.`;

  const serviceLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Trailer rental in ${data.fullName}`,
    serviceType: 'Peer-to-peer trailer rental',
    provider: {
      '@type': 'Organization',
      name: 'TowShare',
      url: SITE_ORIGIN,
      logo: `${SITE_ORIGIN}/TowShareIcon.png`,
    },
    areaServed: {
      '@type': 'Place',
      name: data.fullName,
      address: {
        '@type': 'PostalAddress',
        addressLocality: data.name,
        addressRegion: 'ON',
        addressCountry: 'CA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: data.lat,
        longitude: data.lng,
      },
    },
    url: `${SITE_ORIGIN}${canonicalPath}`,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `Popular trailer types in ${data.name}`,
      itemListElement: data.popularTypes.map((t) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: `${t.label} trailer rental in ${data.name}` },
        url: `${SITE_ORIGIN}/search?type=${t.slug}&near=${encodeURIComponent(`${data.name}, Ottawa, ON`)}`,
      })),
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'TowShare', item: SITE_ORIGIN },
      { '@type': 'ListItem', position: 2, name: 'Trailer rental', item: `${SITE_ORIGIN}/search` },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${parentCity.name}, ON`,
        item: `${SITE_ORIGIN}/trailer-rental/${parentCity.slug}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: data.name,
        item: `${SITE_ORIGIN}${canonicalPath}`,
      },
    ],
  };

  usePageMeta({
    title,
    description,
    canonicalPath,
    keywords: `trailer rental ${data.name}, utility trailer ${data.name}, dump trailer ${data.name}, car hauler ${data.name}, ${data.name} U-Haul alternative, ${data.fsas.join(' ')}, TowShare ${data.name}`,
    jsonLd: [serviceLd, faqLd, breadcrumbLd],
  });

  const searchHref = `/search?lat=${data.lat.toFixed(5)}&lng=${data.lng.toFixed(5)}&radiusKm=15`;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-navy via-navy-600 to-navy-700 text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 30%, #ff751f 0, transparent 40%), radial-gradient(circle at 80% 70%, #ff751f 0, transparent 30%)',
            }}
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <nav className="text-xs text-navy-100/80 mb-4 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-orange">
              TowShare
            </Link>
            <span>/</span>
            <Link to="/search" className="hover:text-orange">
              Trailer rental
            </Link>
            <span>/</span>
            <Link
              to={`/trailer-rental/${parentCity.slug}`}
              className="hover:text-orange"
            >
              {parentCity.name}, ON
            </Link>
            <span>/</span>
            <span className="text-white">{data.name}</span>
          </nav>
          <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
            Trailer rental · {data.fullName}
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight max-w-3xl mb-4 leading-tight">
            Rent a trailer in {data.name}
            <br />
            <span className="text-orange">from a neighbour, not a yard.</span>
          </h1>
          <p className="text-base sm:text-lg text-navy-100 max-w-2xl mb-6 md:mb-8">
            {data.tagline}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to={searchHref} className="btn-primary px-6">
              See {data.name} trailers
            </Link>
            <Link
              to="/list-your-trailer"
              className="btn-ghost px-6 !text-white !border-white/30 hover:!bg-white/10"
            >
              List your trailer
            </Link>
          </div>
          {data.fsas.length > 0 && (
            <p className="mt-6 text-xs text-navy-100/80">
              FSAs: {data.fsas.map((f) => <span key={f} className="font-mono mr-2">{f}</span>)}
            </p>
          )}
        </div>
      </section>

      {/* Live listings — fresh content per crawl */}
      <LiveListingsStrip
        lat={data.lat}
        lng={data.lng}
        radiusKm={15}
        heading={`Available now in ${data.name}`}
        emptyHint={`No active listings in ${data.name} this minute — try ${parentCity.name} city-wide.`}
      />

      {/* Popular types */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-navy mb-2">
          Most-rented trailer types in {data.name}
        </h2>
        <p className="text-navy-600 mb-6 text-sm">
          Tap a category to filter by trailer type within 15&nbsp;km.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {data.popularTypes.map((t) => (
            <Link
              key={t.slug}
              to={`/search?type=${t.slug}&lat=${data.lat.toFixed(5)}&lng=${data.lng.toFixed(5)}&radiusKm=15`}
              className="card p-5 hover:border-orange hover:shadow-md transition-all group"
            >
              <div className="text-3xl mb-2">{t.emoji}</div>
              <div className="font-semibold text-navy group-hover:text-orange transition-colors">
                {t.label}
              </div>
              <div className="text-xs text-navy-500 mt-1">{t.example}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why TowShare here */}
      <section className="bg-navy-50/40 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
              What renters use trailers for in {data.name}
            </p>
            <h2 className="text-xl md:text-2xl font-bold text-navy mb-4">
              Common trips
            </h2>
            <ul className="space-y-2.5 text-navy-700 text-sm">
              {data.useCases.map((u) => (
                <li key={u} className="flex gap-3">
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
              Why TowShare here
            </p>
            <h2 className="text-xl md:text-2xl font-bold text-navy mb-4">
              Beats the yard
            </h2>
            <ul className="space-y-2.5 text-navy-700 text-sm">
              {data.whyHere.map((w) => (
                <li key={w} className="flex gap-3">
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
            {data.competitorAddresses && data.competitorAddresses.length > 0 && (
              <p className="text-xs text-navy-500 mt-4 leading-snug">
                Beats a yard rental at{' '}
                {data.competitorAddresses.map((a, i) => (
                  <span key={a}>
                    {i > 0 ? ', ' : ''}
                    <span className="font-medium text-navy-700">{a}</span>
                  </span>
                ))}{' '}
                — neighbourhood pickup, no commute, no lineup.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Sub-areas */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-xl md:text-2xl font-bold text-navy mb-2">
          Pickup pockets in and around {data.name}
        </h2>
        <p className="text-navy-600 mb-5 text-sm">
          TowShare matches you with owners within a 10&nbsp;km radius of your address.
          Common pickup pockets:
        </p>
        <ul className="flex flex-wrap gap-2">
          {data.subAreas.map((s) => (
            <li
              key={s}
              className="px-3 py-1.5 rounded-full bg-navy-50 text-navy-700 text-sm"
            >
              {s}
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="bg-navy-50/40 py-12">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-bold text-navy mb-6 text-center">
            Frequently asked — {data.name}
          </h2>
          <div className="space-y-3">
            {data.faqs.map((f) => (
              <details
                key={f.q}
                className="group card p-4 open:border-orange open:shadow-md"
              >
                <summary className="cursor-pointer font-semibold text-navy flex items-center justify-between gap-4 text-sm">
                  {f.q}
                  <span className="text-orange text-xl transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-navy-700 leading-relaxed text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Ready to rent in {data.name}?
          </h2>
          <p className="text-navy-100 max-w-xl mx-auto mb-5 text-sm">
            Search verified trailers within 15&nbsp;km of you. Pay through Stripe, pick up
            from a neighbour, hit the road.
          </p>
          <Link to={searchHref} className="btn-primary px-8">
            Search {data.name} trailers
          </Link>
        </div>
      </section>
    </div>
  );
}
